import { Button, EditIcon, Table, type TableColumns } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type LicensedProductTypes, Products } from 'pages/Settings/subpages/types';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { bookedBBBSlots } from 'utils/bookedBBBSlots';
import { UserStatus } from '../../../client/gql/graphql';
import { useIsDesktop } from '../../../hooks/useIsDesktop';

const BookingsTableFieldsDocument = graphql(`
  fragment BookingsTableFields on Query {
    organizations(where: { uuid: $organizationUuid }) {
      uuid
      __typename
      licensedProducts {
        __typename
        ... on Bildungsplattform {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Nextcloud {
          uuid
          serverUri
          profilesAggregate(where: { NOT: { email_IN: ["admin@tks.eu"] } }) {
            __typename
            count
          }
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on ThreemaBroadcast {
          uuid
          threemaId
          broadcastNickname
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Threema {
          uuid
          licenseCount
          licenseName
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
          profilesConnection(where: { edge: { enabled: true } }) {
            edges {
              node {
                uuid
              }
            }
          }
        }
        ... on BigBlueButton {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Zoom {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
      }
    }
    
    profiles(
      where: {
        organization: { uuid: $organizationUuid }
        organizationRoles_INCLUDES: "STUDENT"
      }
    ) {
      __typename
      uuid
        user {
          __typename
          uuid
          status
      }
    }
  }
`);

type BookingsTableType = {
  module: LicensedProductTypes;
  comment?: string;
  start: string;
  end: string;
  productVersion: string;
  usage: string;
};

type BookingsTableProps = {
  onEdit: (module: LicensedProductTypes) => void;
  onDelete: (module: LicensedProductTypes) => void;
  query: FragmentType<typeof BookingsTableFieldsDocument> | undefined;
};

export const BookingsTable = ({ onEdit, onDelete, query }: BookingsTableProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const fragment = getFragmentData(BookingsTableFieldsDocument, query);

  const licensedProducts = fragment?.organizations[0]?.licensedProducts;
  const permissions = usePermissionChecker();

  const { organizationProducts, procuratEnabled, planungEnabled } = useContext(OrganizationConfigContext);

  const bildungsplattform = organizationProducts?.find((p) => p.__typename === 'Bildungsplattform');
  const bildungsplattformLicense =
    bildungsplattform?.organizationConnection.edges[0]?.properties.version ?? 'community';

  const possibleLicensedProducts: BookingsTableType[] = useMemo(() => {
    const possibleLicensedProducts = Object.keys(Products).map((product) => {
      return {
        __typename: Products[product as keyof typeof Products],
      };
    });

    return possibleLicensedProducts
      .map((product) => {
        const booked = licensedProducts?.find((d) => d.__typename === product.__typename);

        const start = booked?.organizationConnection?.edges[0]?.properties.notBefore;
        const end = booked?.organizationConnection?.edges[0]?.properties.notAfter;

        let usage = '';
        let billable = '-';
        let productVersion: string = booked?.organizationConnection?.edges[0]?.properties.version?.toLowerCase() ?? '-';
        let comment = '';

        if (product.__typename === 'TK-Planung') {
          productVersion = planungEnabled ? t('common.yes') : t('common.no');
          comment = t('institution.interfaceComment');
        }

        if (booked?.__typename === 'Nextcloud') {
          usage = booked?.profilesAggregate?.count?.toString() ?? '';
          comment = booked?.serverUri ?? '';
          productVersion = usage;
          billable = usage;
        }

        if (product.__typename === 'BigBlueButton') {
          let bbbBillableConnections: number = bookedBBBSlots(productVersion);
          if (isNaN(bbbBillableConnections)) bbbBillableConnections = -1;

          if (bbbBillableConnections === 0) {
            comment = 'SelfHosted';
          } else if (bbbBillableConnections > 0) {
            if (bildungsplattformLicense === 'pro' && bbbBillableConnections) {
              bbbBillableConnections -= 50;
            }
            billable = Math.max(bbbBillableConnections ?? 0, 0).toString();
          }
        }
        if (product.__typename === 'Bildungsplattform') {
          productVersion = t('app.' + bildungsplattformLicense);
          usage = fragment?.profiles?.filter((p) => p.user?.status !== UserStatus.None).length.toString() ?? '0';
          billable = usage;
          if (bildungsplattformLicense === 'community') {
            //
          }
        }
        if (product.__typename === 'Procurat!') {
          comment = procuratEnabled ?? '';
        }
        if (booked?.__typename === 'Threema') {
          billable = (booked?.profilesConnection.edges.length ?? 0).toString();
          if (booked?.licenseCount) {
            usage = billable + '/' + booked?.licenseCount;
            comment = booked?.licenseName ? 'Lizenzpaket: ' + booked.licenseName : '';
          } else {
            usage = billable;
          }
        }
        if (booked?.__typename === 'ThreemaBroadcast') {
          comment = booked?.broadcastNickname
            ? booked?.broadcastNickname + ': ' + booked?.threemaId
            : (booked?.threemaId ?? '');
        }

        return {
          module: product.__typename as LicensedProductTypes,
          start: start ? dayjs(start).format('DD.MM.YYYY') : '',
          end: booked && end ? dayjs(end).format('DD.MM.YYYY') : booked ? '-' : '',
          comment,
          productVersion,
          billable,
          usage,
        };
      })
      .sort((a, b) => a.module.localeCompare(b.module));
  }, [bildungsplattformLicense, licensedProducts, procuratEnabled, planungEnabled, fragment, t]);

  const columns = useMemo((): TableColumns<BookingsTableType>[] => {
    return [
      {
        id: 'module',
        header: t('institution.module'),
        accessorKey: 'module',
        size: 200,
      },
      {
        id: 'comment',
        header: '',
        accessorKey: 'comment',
        canExpand: true,
      },
      {
        id: 'start',
        header: t('institution.bookedDate'),
        accessorKey: 'start',
        size: 100,
        alignment: 'right',
      },
      {
        id: 'end',
        header: t('common.activeUntil'),
        accessorKey: 'end',
        size: 100,
        alignment: 'right',
      },
      {
        id: 'booked',
        accessorKey: 'productVersion',
        header: t('institution.booked'),
        size: 100,
        alignment: 'right',
      },
      {
        id: 'billable',
        accessorKey: 'billable',
        header: t('institution.billable'),
        size: 100,
        alignment: 'right',
      },
      {
        id: 'usage',
        accessorKey: 'usage',
        header: t('institution.usage'),
        size: 100,
        alignment: 'right',
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <Table<BookingsTableType>
      columns={columns}
      data={possibleLicensedProducts}
      isDesktop={isDesktop}
      customPadding='var(--spacing-6)'
      lastCol={(row) => {
        return (row.original.module === 'Bildungsplattform' && permissions?.isOmniAdmin()) ||
          (row.original.module === 'BigBlueButton' && permissions?.isOmniAdmin()) ||
          // (row.original.module === 'TK-Planung' && permissions?.isOmniAdmin()) ||
          (row.original.module === 'Procurat!' && permissions?.isOmniAdmin()) ||
          (row.original.module === 'Threema' && permissions?.isOmniAdmin()) ||
          (row.original.module === 'ThreemaBroadcast' && permissions?.isOmniAdmin()) ||
          (row.original.module === 'Nextcloud' && permissions?.isOmniAdmin()) ? (
          <>
            <Button hierarchy='ghost' icon={<EditIcon />} onClick={() => onEdit(row.original.module)} />
            {/*<Button hierarchy='ghost' icon={<DeleteIcon />} onClick={() => onDelete(row.original.module)} />*/}
          </>
        ) : undefined;
      }}
    />
  );
};
